import { useContext } from 'react'

import { VALIDATE_CHECKOUT_BLOCK_DISCOUNT_STORES } from '../constants/stores'
import { DataContext } from '../context/DataContext'
import cmsInstance from '../helpers/cmsInstance'
import { resolveScraperVersion } from '../helpers/scrapper'

export const useCmsAdapter = () => {
  const {
    userInfo,
    ecommerceInfo,
    companyInfo,
    addressInfo,
    cartData,
    configurations,
    scraperAssignment,
    didFallbackFromContainer
  } = useContext(DataContext)
  const { pinflagApiKey, companyName } = companyInfo

  const scrapperMetricsData = async (customerData, abandonedStep) => {
    const metricsData = await customerData ??
      {
        discountCodes: [],
        shippingRate: '',
        completeData: {},
        url: ''
      }

    return {
      domain: ecommerceInfo.domain,
      url: metricsData.url,
      cartToken: ecommerceInfo.cartToken,
      discountCodes: metricsData.discountCodes,
      completeData: metricsData.completeData,
      abandonedStep
    }
  }

  const getMagentoCouponFreeShipping = (couponCode) => {
    return cmsInstance.get(
      `/magento/coupon/free-shipping?couponCode=${encodeURIComponent(couponCode)}`
    )
  }

  const getVariantLocations = (variantId) => {
    return cmsInstance.post('/shopify/locations', {
      pinflagApiKey,
      variantId: `gid://shopify/ProductVariant/${variantId}`
    })
  }

  const getCheckout = checkoutId => {
    return cmsInstance.get(
      `/shopify/checkout?checkoutId=${checkoutId}`
    )
  }

  const createCheckout = input => {
    return cmsInstance.post('/shopify/checkout', {
      input,
      pinflagApiKey
    })
  }

  const updateEmailCheckout = (checkoutId, email) => {
    return cmsInstance.post('/shopify/checkout/update-email', {
      checkoutId,
      email,
      pinflagApiKey
    })
  }

  const setItemsCheckout = (checkoutId, oldLineItemsId, newLineItems) => {
    return cmsInstance.post('/shopify/checkout/set-items', {
      checkoutId,
      oldLineItemsId,
      newLineItems,
      pinflagApiKey
    })
  }

  const updateAddressCheckout = (checkoutId, address) => {
    return cmsInstance.post('/shopify/checkout/update-address', {
      checkoutId,
      address,
      pinflagApiKey
    })
  }

  const getShippingRates = checkoutId => {
    return cmsInstance.get(
      `/shopify/checkout/get-shipping-rates?checkoutId=${checkoutId}`
    )
  }

  const setShippingRate = (checkoutId, shippingRateHandle) => {
    return cmsInstance.post('/shopify/checkout/set-shipping-rate', {
      checkoutId,
      shippingRateHandle,
      pinflagApiKey
    })
  }

  const sendCartNotesShopify = (checkoutId, notes, isGiftWrapping, giftNote) => {
    const { cms } = ecommerceInfo
    if (cms !== 'shopify') return
    return cmsInstance.post('/shopify/checkout/update-attributes', {
      checkoutId,
      pinflagApiKey,
      input: isGiftWrapping
        ? {
            note: notes,
            customAttributes: giftNote
              ? [
                  {
                    key: 'gift-wrapping',
                    value: 'on'
                  },
                  {
                    key: 'Gift Note',
                    value: giftNote
                  }
                ]
              : [
                  {
                    key: 'gift-wrapping',
                    value: isGiftWrapping ? 'on' : 'off'
                  }
                ]
          }
        : { note: notes }
    })
  }

  const updateCustomAttributesShopify = (checkoutId, input) => {
    const { cms } = ecommerceInfo
    if (cms !== 'shopify') return
    return cmsInstance.post('/shopify/checkout/update-attributes', {
      checkoutId,
      pinflagApiKey,
      input
    })
  }

  const ignoreProfileDataVtex = orderFormId => {
    return cmsInstance.post('/vtex/order/ignore-personal-data', {
      orderFormId,
      pinflagApiKey
    })
  }

  const getOrderFormVtex = (orderFormId) => {
    return cmsInstance.get(`/vtex/order-form?orderFormId=${orderFormId}`)
  }

  const getPaymentMethods = (cms) => {
    if (cms === 'shopify') return cmsInstance.get('/shopify/store-payment-methods')
    if (cms.includes('woocommerce')) return cmsInstance.get('/woocommerce/store-payment-methods')
  }

  const getShopifyConfigurations = () => {
    return cmsInstance.get('/shopify/configurations')
  }

  const getBankDepositPaymentUrl = (email) => {
    return cmsInstance.get(`/shopify/bank-deposit-order-status-url?email=${email}`)
  }

  const initScrapperCall = (domain, url, uuidConnection, encryptedData, checkoutAutomationAccount) => {
    return cmsInstance.post('/shopify/checkout-automation', {
      domain,
      url,
      uuid: uuidConnection,
      encryptedData,
      cartToken: ecommerceInfo.cartToken,
      siteCookies: ecommerceInfo.siteCookies,
      checkoutAutomationAccount
    })
  }

  const validateDiscountCodesShopify = (discountCodes, lineItems, email) => {
    return cmsInstance.post('/shopify/cart/discount-codes', {
      pinflagApiKey,
      discountCodes,
      lineItems,
      email
    })
  }

  const availableShippingRates = (input, encryptedAddressData) => {
    return cmsInstance.post('/shopify/available-shipping-rates', {
      input,
      pinflagApiKey,
      encryptedAddressData
    })
  }

  const validateDiscountForCustomerShopify = (discountCode, email, type) => {
    return cmsInstance.post('/shopify/cart/customer-discount-code', {
      pinflagApiKey,
      discountCode,
      email,
      type
    })
  }

  const trackingPinmap = async ({ body, checkoutUsed }) => {
    const oldCurrentUrl = ecommerceInfo.currentUrl || body?.currentUrl || '/'
    const getSlice = oldCurrentUrl.indexOf('?') === -1 ? oldCurrentUrl.length : oldCurrentUrl.indexOf('?')
    const currentUrl = oldCurrentUrl.slice(0, getSlice)

    try {
      await cmsInstance.post('/tracking/pinmap', {
        id: cartData.cartId,
        storeName: companyName,
        device: navigator.userAgent,
        maxTouchPoints: navigator.maxTouchPoints,
        wasPinmapLeak: false,
        timeOpenFirstStep: new Date(),
        cms: ecommerceInfo.cms,
        checkoutUsed,
        cartData: {
          total: cartData.total,
          subtotal: cartData.subtotal,
          lines: cartData.lines,
          itemsCount: cartData.itemsCount,
          totalWeight: cartData.totalWeight,
          cartId: cartData.cartId
        },
        pinflagApiKey: companyInfo.pinflagApiKey,
        ...body,
        currentUrl,
        scraperVersion: resolveScraperVersion(scraperAssignment, didFallbackFromContainer)
      })
    } catch (err) {
      console.log('hubo un error en el log', err)
    }
  }

  const trackingSecondStep = async (body, customerData, abandonedStep) => {
    try {
      const metricsData = await scrapperMetricsData(customerData, abandonedStep)
      await cmsInstance.post('/tracking/pinmap/update', {
        id: cartData.cartId,
        storeName: companyName,
        cms: ecommerceInfo.cms,
        cartData,
        userInfo,
        pinflagApiKey: companyInfo.pinflagApiKey,
        customerData: configurations?.useAbandonedCarts ? metricsData : null,
        ...body,
        scraperVersion: resolveScraperVersion(scraperAssignment, didFallbackFromContainer)
      })
    } catch (err) {
      console.log('hubo un error en el log', err)
    }
  }

  const trackingThirdStep = async (body, customerData, abandonedStep) => {
    try {
      const metricsData = await scrapperMetricsData(customerData, abandonedStep)
      await cmsInstance.post('/tracking/pinmap/update', {
        id: cartData.cartId,
        storeName: companyName,
        cms: ecommerceInfo.cms,
        cartData,
        shippingInfo: addressInfo,
        pinflagApiKey: companyInfo.pinflagApiKey,
        customerData: configurations?.useAbandonedCarts ? metricsData : null,
        ...body,
        scraperVersion: resolveScraperVersion(scraperAssignment, didFallbackFromContainer)
      })
    } catch (err) {
      console.log('hubo un error en el log', err)
    }
  }

  const trackingLeak = (body) => {
    cmsInstance.post('/tracking/pinmap/update', {
      id: cartData.cartId,
      storeName: companyName,
      cms: ecommerceInfo.cms,
      cartData,
      wasPinmapLeak: true,
      timePinmapLeak: new Date(),
      pinflagApiKey: companyInfo.pinflagApiKey,
      ...body,
      scraperVersion: resolveScraperVersion(scraperAssignment, didFallbackFromContainer)
    }).catch((err) => {
      console.log('hubo un error en el log', err)
    })
  }

  const validateEmail = async (email) => {
    let retries = 3
    while (retries > 0) {
      try {
        const response = await cmsInstance.post('/validate/email', { email })
        const { validated } = response?.data ?? {}
        if (validated === false) {
          return false
        }
        return true
      } catch (err) {
        retries--
        if (retries === 0) {
          return true
        }
      }
    }
    return true
  }

  const getLocation = async (locationId) => {
    return cmsInstance.post('/shopify/location', {
      pinflagApiKey: companyInfo.pinflagApiKey,
      locationId
    })
  }

  const validateDiscountForCustomerWoocomerce = (email, codes) => {
    return cmsInstance.post('/woocommerce/coupons', {
      codes,
      email
    })
  }

  const setClientDataVtex = (orderFormId, clientData) => {
    return cmsInstance.post('/vtex/order-form/client-data', {
      orderFormId,
      pinflagApiKey,
      clientData
    })
  }

  const setShippingDataVtex = async (orderFormId, shippingData, logisticsInfo) => {
    try {
      const response = await cmsInstance.post('/vtex/order-form/shipping-data', {
        orderFormId,
        pinflagApiKey,
        shippingData,
        logisticsInfo
      })
      return response
    } catch (err) {
      console.log('Este es el error', err)
      return false
    }
  }

  const applyDiscountCodeVtex = (orderFormId, code) => {
    return cmsInstance.post('/vtex/order-form/apply-discount-code', {
      pinflagApiKey,
      orderFormId,
      code
    })
  }

  const validateGiftCartShopify = (code) => {
    return cmsInstance.post('/shopify/gift-card/validate', {
      pinflagApiKey,
      code
    })
  }

  const setPaymentDataVtex = (orderFormId, paymentData, giftCards) => {
    return cmsInstance.post('/vtex/order-form/payment-data', {
      orderFormId,
      pinflagApiKey,
      paymentData,
      giftCards
    })
  }

  const getShopifyAutomaticDiscounts = (subtotal, itemsQuantity, lineItems, email, address) => {
    const checkoutBlockParams = VALIDATE_CHECKOUT_BLOCK_DISCOUNT_STORES.includes(companyName)
      ? {
          ...(lineItems && { lineItems }),
          ...(email && { email }),
          ...(address && { address })
        }
      : {}
    return cmsInstance.post('/shopify/automatic-discounts', {
      pinflagApiKey,
      subtotal,
      itemsQuantity,
      ...checkoutBlockParams
    })
  }

  const getWeightVtex = (items, pinflagApiKey) => {
    return cmsInstance.post('/vtex/items-weights', {
      pinflagApiKey,
      items
    })
  }

  const getCartShopify = (cartId) => {
    return cmsInstance.get(`/shopify/cart?cartId=${cartId}`)
  }

  return {
    getMagentoCouponFreeShipping,
    getVariantLocations,
    getCheckout,
    createCheckout,
    updateEmailCheckout,
    setItemsCheckout,
    updateAddressCheckout,
    getShippingRates,
    setShippingRate,
    ignoreProfileDataVtex,
    getPaymentMethods,
    initScrapperCall,
    sendCartNotesShopify,
    updateCustomAttributesShopify,
    validateDiscountCodesShopify,
    availableShippingRates,
    validateDiscountForCustomerShopify,
    trackingPinmap,
    trackingSecondStep,
    trackingThirdStep,
    trackingLeak,
    validateEmail,
    getLocation,
    validateDiscountForCustomerWoocomerce,
    setClientDataVtex,
    getOrderFormVtex,
    setShippingDataVtex,
    applyDiscountCodeVtex,
    validateGiftCartShopify,
    setPaymentDataVtex,
    getShopifyAutomaticDiscounts,
    getShopifyConfigurations,
    getBankDepositPaymentUrl,
    getWeightVtex,
    getCartShopify
  }
}
